/**
 * LinkedTableSidebar - Vertical icon strip sidebar for linked table configuration
 *
 * Structure:
 * ┌───┬──────────────────────┐
 * │ℹ️│ Active section content │
 * │🔗│                        │
 * │$x│                        │
 * │🕐│                        │
 * │📊│                        │
 * │🔽│                        │
 * │  ├────────────────────────┤
 * │  │ [Save]      [Cancel]   │
 * └───┴────────────────────────┘
 */

import { useMemo } from 'react';
import { Info, GitBranch, Variable, Clock, Columns3, Filter, Save, X } from 'lucide-react';
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from '@loader/ui';

import type { LinkedTableSidebarProps } from './types';

import { InfoSection } from '@/features/linkedTables/editLinkedTable/components/LinkedTableConfigSidebar/InfoSection';
import { RelationshipsSection } from '@/features/linkedTables/editLinkedTable/components/LinkedTableConfigSidebar/RelationshipsSection';
import { VariablesSection } from '@/features/linkedTables/editLinkedTable/components/LinkedTableConfigSidebar/VariablesSection';
import { VersionsSection } from '@/features/linkedTables/editLinkedTable/components/LinkedTableConfigSidebar/VersionsSection';
import { FiltersSection } from '@/features/linkedTables/editLinkedTable/components/LinkedTableConfigSidebar/FiltersSection';
import { ColumnsTab } from '@/features/linkedTables/editLinkedTable/components/LinkedTableConfigSidebar/ColumnsTab';
import { useLinkedTableSidebarStore, type SidebarSection } from '@/features/linkedTables/editLinkedTable/stores/useLinkedTableSidebarStore';

/** Section configuration for the icon strip */
const SECTIONS: { id: SidebarSection; icon: typeof Info; label: string; description: string }[] = [
  { id: 'columns', icon: Columns3, label: 'Columns', description: 'Configure row groups, value aggregations, and calculated columns.' },
  { id: 'filters', icon: Filter, label: 'Filters', description: 'Narrow results by applying conditions on fields.' },
  { id: 'relationships', icon: GitBranch, label: 'Relationships', description: 'Join other tables to combine data across sources.' },
  { id: 'variables', icon: Variable, label: 'Variables', description: 'Define dynamic parameters that can be set at query time.' },
  { id: 'general', icon: Info, label: 'General', description: 'Name and description of this linked table.' },
  { id: 'versions', icon: Clock, label: 'Versions', description: 'Choose which version of each table to query.' },
];

function SectionHeader({ section }: { section: SidebarSection }) {
  const config = SECTIONS.find((s) => s.id === section);
  if (!config) return null;
  const Icon = config.icon;
  return (
    <div className="-mx-3 mb-3 border-b px-3 pb-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-medium">{config.label}</h3>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{config.description}</p>
    </div>
  );
}

export function LinkedTableSidebar({
  canEdit = true,

  // General props
  linkedTableName,
  linkedTableDescription,
  onUpdateName,
  onUpdateDescription,

  // Relationships props
  relationships,
  currentLinkedTableId,
  currentTableId,
  availableFields,

  // Variables props
  variables = [],
  onAddVariable,
  onUpdateVariable,
  onRemoveVariable,

  // Versions props
  table_id,
  tables,
  versionSelections,
  onUpdateVersionSelection,
  onAddRelationship,
  onUpdateRelationship,
  onRemoveRelationship,

  // Columns props
  calculated_columns,
  row_groups,
  row_group_sorts,
  values,
  onAddToRowGroups,
  onRemoveFromRowGroups,
  onReorderRowGroups,
  onUpdateRowGroupSort,
  onAddToValues,
  onRemoveFromValues,
  onReorderValues,
  onUpdateValueFunction,
  onAddCalculatedColumn,
  onUpdateCalculatedColumn,
  onRemoveCalculatedColumn,

  // Filters props
  filters,
  linkedTableId,
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter,

  // Action props
  pendingChangesCount = 0,
  canSave = true,
  incompleteCounts,
  onSave,
  onCancel,
}: LinkedTableSidebarProps) {
  const { activeSection, setActiveSection } = useLinkedTableSidebarStore();

  const incompleteFilterIds = useMemo(
    () => filters.filter((f) => !f.field_id || !f.operator).map((f) => f.id),
    [filters]
  );

  const incompleteRelationshipIds = useMemo(
    () => relationships.filter((r) => !r.target_id || !r.target_type || !r.alias).map((r) => r.id),
    [relationships]
  );

  return (
    <div className="bg-sidebar text-sidebar-foreground flex h-full w-94 shrink-0 overflow-hidden">
      {/* Content panel */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-l">
        <div className="flex min-h-0 flex-1 flex-col">
          {activeSection === 'general' && (
            <div className="flex-1 overflow-auto p-3">
              <SectionHeader section="general" />
              <InfoSection
                canEdit={canEdit}
                linkedTableName={linkedTableName}
                linkedTableDescription={linkedTableDescription}
                onUpdateName={onUpdateName}
                onUpdateDescription={onUpdateDescription}
              />
            </div>
          )}

          {activeSection === 'relationships' && (
            <div className="flex-1 overflow-auto p-3">
              <SectionHeader section="relationships" />
              <RelationshipsSection
                canEdit={canEdit}
                relationships={relationships}
                availableFields={availableFields}
                currentLinkedTableId={currentLinkedTableId}
                currentTableId={currentTableId}
                primaryTableAlias={tables[0]?.alias}
                incompleteRelationshipIds={incompleteRelationshipIds}
                onAddRelationship={onAddRelationship}
                onUpdateRelationship={onUpdateRelationship}
                onRemoveRelationship={onRemoveRelationship}
              />
            </div>
          )}

          {activeSection === 'variables' && (
            <div className="flex-1 overflow-auto p-3">
              <SectionHeader section="variables" />
              <VariablesSection
                canEdit={canEdit}
                variables={variables}
                onAddVariable={onAddVariable}
                onUpdateVariable={onUpdateVariable}
                onRemoveVariable={onRemoveVariable}
              />
            </div>
          )}

          {activeSection === 'versions' && (
            <div className="flex-1 overflow-auto p-3">
              <SectionHeader section="versions" />
              <VersionsSection
                table_id={table_id}
                tables={tables}
                relationships={relationships}
                versionSelections={versionSelections || {}}
                onUpdateVersionSelection={onUpdateVersionSelection || (() => {})}
              />
            </div>
          )}

          {activeSection === 'columns' && (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="shrink-0 px-3 pt-3">
                <SectionHeader section="columns" />
              </div>
              <ColumnsTab
                canEdit={canEdit}
                availableFields={availableFields}
                tables={tables}
                calculated_columns={calculated_columns}
                relationships={relationships}
                variables={variables}
                row_groups={row_groups}
                row_group_sorts={row_group_sorts}
                values={values}
                onAddToRowGroups={onAddToRowGroups}
                onRemoveFromRowGroups={onRemoveFromRowGroups}
                onReorderRowGroups={onReorderRowGroups}
                onUpdateRowGroupSort={onUpdateRowGroupSort}
                onAddToValues={onAddToValues}
                onRemoveFromValues={onRemoveFromValues}
                onReorderValues={onReorderValues}
                onUpdateValueFunction={onUpdateValueFunction}
                onAddCalculatedColumn={onAddCalculatedColumn}
                onUpdateCalculatedColumn={onUpdateCalculatedColumn}
                onRemoveCalculatedColumn={onRemoveCalculatedColumn}
              />
            </div>
          )}

          {activeSection === 'filters' && (
            <div className="flex min-h-0 flex-1 flex-col overflow-auto p-3">
              <SectionHeader section="filters" />
              <FiltersSection
                canEdit={canEdit}
                filters={filters}
                availableFields={availableFields}
                calculated_columns={calculated_columns}
                linkedTableId={linkedTableId}
                versionSelections={versionSelections}
                incompleteFilterIds={incompleteFilterIds}
                variables={variables}
                onAddFilter={onAddFilter}
                onUpdateFilter={onUpdateFilter}
                onRemoveFilter={onRemoveFilter}
              />
            </div>
          )}
        </div>

        {/* Action Buttons - Hidden for viewers */}
        {canEdit && (
          <div className="shrink-0 border-t p-2">
            <div className="flex gap-2">
              <Button
                className="flex-1"
                onClick={onSave}
                disabled={pendingChangesCount === 0 || !canSave}
                title={
                  !canSave
                    ? (() => {
                        const issues: string[] = [];
                        if (incompleteCounts && incompleteCounts.filters > 0) {
                          issues.push(`${incompleteCounts.filters} incomplete filter(s)`);
                        }
                        if (incompleteCounts && incompleteCounts.calculated_columns > 0) {
                          issues.push(
                            `${incompleteCounts.calculated_columns} incomplete calculated column(s)`
                          );
                        }
                        if (incompleteCounts && incompleteCounts.relationships > 0) {
                          issues.push(`${incompleteCounts.relationships} incomplete relationship(s)`);
                        }
                        return issues.length > 0
                          ? `Cannot save: ${issues.join(', ')}. Please complete or remove them.`
                          : 'Please complete all items before saving';
                      })()
                    : pendingChangesCount === 0
                      ? 'No changes to save'
                      : 'Save changes'
                }
              >
                <Save className="mr-2 h-4 w-4" />
                Save
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={onCancel}
                disabled={pendingChangesCount === 0}
              >
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Vertical icon strip (right side) */}
      <TooltipProvider delayDuration={300}>
        <div className="flex w-12 shrink-0 flex-col border-l bg-sidebar">
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;

            return (
              <Tooltip key={section.id}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setActiveSection(section.id)}
                    className={cn(
                      'flex h-9 w-full items-center justify-center transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary shadow-[inset_2px_0_0_0_var(--color-primary)]'
                        : 'text-muted-foreground hover:bg-sidebar-accent/50 hover:text-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" className="text-xs">
                  {section.label}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>
    </div>
  );
}

// Re-export types for convenience
export type { LinkedTableSidebarProps } from './types';
